/**
 * Turns an exercise's free-text muscle names into everything the renderers
 * need: which muscles are lit, which face to show, and the paths to draw in
 * paint order.
 *
 * Deliberately colourless. See `muscle-map.types.ts` for why.
 */

import { musclesForName } from './muscle-aliases';
import { BACK_MUSCLES, FRONT_MUSCLES } from './muscle-geometry';
import { MUSCLE_IDS, MUSCLE_LABELS } from './muscle-ids';

import type {
  MuscleChip,
  MuscleFace,
  MuscleGroupShapes,
  MuscleId,
  MuscleMapGroup,
  MuscleMapModel,
  MuscleMapPath,
  MuscleRole,
  MuscleSelection,
} from './muscle-map.types';

/** Paint order. SVG has no z-index, so the renderer must emit in this order. */
const Z_BY_ROLE: Readonly<Record<MuscleRole, number>> = {
  none: 0,
  secondary: 1,
  primary: 2,
};
const Z_SELECTED = 3;

const CATALOGUE: Readonly<Record<MuscleFace, readonly MuscleGroupShapes[]>> = {
  front: FRONT_MUSCLES,
  back: BACK_MUSCLES,
};

const FRONT_IDS = new Set<MuscleId>(FRONT_MUSCLES.map((group) => group.id));
const BACK_IDS = new Set<MuscleId>(BACK_MUSCLES.map((group) => group.id));

function titleCase(value: string): string {
  return value.replace(/\b\w/g, (char) => char.toUpperCase());
}

function unique<T>(values: T[]): T[] {
  return [...new Set(values)];
}

export interface MuscleMapSource {
  /** The exercise's primary target, e.g. `'Pectorals'`. */
  target: string | null | undefined;
  secondaryMuscles: string[] | null | undefined;
}

/** Resolve an exercise's muscle names into the model the renderers consume. */
export function buildMuscleMapModel({
  target,
  secondaryMuscles,
}: MuscleMapSource): MuscleMapModel {
  const secondaryNames = secondaryMuscles ?? [];
  const primary = target ? musclesForName(target) : [];
  const secondaryAll = unique(secondaryNames.flatMap(musclesForName));

  return {
    primary,
    // A muscle already lit as primary must not also render as secondary, or
    // the dimmer fill would paint over the accent one.
    secondary: secondaryAll.filter((id) => !primary.includes(id)),
    primaryLabel: target ? titleCase(target) : null,
    secondaryLabel: secondaryNames.map(titleCase).join(', '),
  };
}

/** Canonical ids, already resolved — the shape an API enum arrives in. */
export interface MuscleIdSource {
  primary: readonly MuscleId[] | null | undefined;
  secondary: readonly MuscleId[] | null | undefined;
}

/**
 * Build the model from ids the API has ALREADY resolved.
 *
 * The alias table exists for free text and must not see these: it matches by
 * lowercase substring, so an enum value like `lower_back` misses the
 * `'lower back'` pattern, falls through to the bare `'back'` entry and lands on
 * the lats. Anything arriving as a `MuscleGroup` enum value — the start-workout
 * options, the session's per-exercise muscles — comes through here instead.
 *
 * Labels come from {@link MUSCLE_LABELS} for the same reason the free-text path
 * title-cases the API's own strings: the display name is the app's, not the
 * catalogue's.
 */
export function muscleMapModelFromIds({
  primary,
  secondary,
}: MuscleIdSource): MuscleMapModel {
  const primaryIds = MUSCLE_IDS.filter((id) => (primary ?? []).includes(id));
  const secondaryIds = MUSCLE_IDS.filter(
    (id) => (secondary ?? []).includes(id) && !primaryIds.includes(id)
  );

  return {
    primary: primaryIds,
    secondary: secondaryIds,
    primaryLabel: primaryIds.map((id) => MUSCLE_LABELS[id]).join(', ') || null,
    secondaryLabel: secondaryIds.map((id) => MUSCLE_LABELS[id]).join(', '),
  };
}

function roleOf(model: MuscleMapModel, id: MuscleId): MuscleRole {
  if (model.primary.includes(id)) return 'primary';
  if (model.secondary.includes(id)) return 'secondary';
  return 'none';
}

/**
 * Which face to open on.
 *
 * Measured across the catalogue, 93% of exercises light muscles on both faces
 * once secondaries are counted — so the full lit set cannot discriminate. The
 * primary mover can: by primary alone, 72% of exercises are cleanly one-sided.
 *
 * Front wins ties, including when the primary is one of the six muscles drawn
 * on both faces (shoulders, traps, forearms, calves, hips, ankles) and when the
 * primary resolves to nothing at all.
 */
export function preferredMuscleView(model: MuscleMapModel): MuscleFace {
  const hasFrontOnly = model.primary.some(
    (id) => FRONT_IDS.has(id) && !BACK_IDS.has(id)
  );
  if (hasFrontOnly) return 'front';

  const hasBackOnly = model.primary.some(
    (id) => BACK_IDS.has(id) && !FRONT_IDS.has(id)
  );
  return hasBackOnly ? 'back' : 'front';
}

/**
 * Whether a face has anything lit on it. Drives the marker on the view
 * toggle's inactive segment, so you can see there is something on the other
 * side without flipping to find out.
 */
export function viewHasLitMuscles(
  model: MuscleMapModel,
  face: MuscleFace
): boolean {
  const ids = face === 'front' ? FRONT_IDS : BACK_IDS;
  return (
    model.primary.some((id) => ids.has(id)) ||
    model.secondary.some((id) => ids.has(id))
  );
}

/**
 * Which faces a muscle is drawn on.
 *
 * Selection can arrive from outside the figure — a chip, a deep link — naming a
 * muscle the visible face does not draw. Without this a renderer would either
 * re-derive the catalogue itself or select something invisible.
 */
export function facesForMuscle(id: MuscleId): MuscleFace[] {
  const faces: MuscleFace[] = [];
  if (FRONT_IDS.has(id)) faces.push('front');
  if (BACK_IDS.has(id)) faces.push('back');
  return faces;
}

/**
 * The paths for one face, already sorted into paint order — lit muscles over
 * unlit, the selected one over everything.
 */
export function buildMuscleMapPaths(
  face: MuscleFace,
  model: MuscleMapModel,
  selectedId?: MuscleId | null
): MuscleMapPath[] {
  const paths = CATALOGUE[face].flatMap((group) => {
    const role = roleOf(model, group.id);
    const isSelected = group.id === selectedId;

    return group.shapes.map((shape, index) => ({
      key: `${group.id}-${index}`,
      d: shape.d,
      rotate: shape.rotate,
      muscleId: group.id,
      role,
      isSelected,
      z: isSelected ? Z_SELECTED : Z_BY_ROLE[role],
    }));
  });

  return paths.sort((a, b) => a.z - b.z);
}

/**
 * The same paths, but gathered per muscle and still in paint order.
 *
 * A muscle is the unit of interaction — you tap the chest, not one half of it —
 * so renderers need its shapes together to hang a single hit target and a
 * single accessible label off. Grouping is safe here because every shape of a
 * muscle shares one `z`, so muscles at the same depth may be freely reordered
 * among themselves without changing what covers what.
 */
export function buildMuscleMapGroups(
  face: MuscleFace,
  model: MuscleMapModel,
  selectedId?: MuscleId | null
): MuscleMapGroup[] {
  const groups = new Map<MuscleId, MuscleMapGroup>();

  for (const path of buildMuscleMapPaths(face, model, selectedId)) {
    const existing = groups.get(path.muscleId);
    if (existing) {
      existing.shapes.push(path);
      continue;
    }

    groups.set(path.muscleId, {
      muscleId: path.muscleId,
      label: MUSCLE_LABELS[path.muscleId],
      role: path.role,
      isSelected: path.isSelected,
      z: path.z,
      shapes: [path],
    });
  }

  return [...groups.values()];
}

/**
 * The "Muscles worked" chips, primary first. Built from the API's names rather
 * than resolved ids so a name the figure cannot place still appears.
 *
 * A name is listed once: a secondary that repeats the primary's name, or an
 * earlier secondary's, is dropped. The API can send `shoulders` as both the
 * target and a secondary, and two chips reading "Shoulders" say nothing the
 * first did not.
 */
export function buildMuscleChips({
  target,
  secondaryMuscles,
}: MuscleMapSource): MuscleChip[] {
  const chips: MuscleChip[] = [];
  const listed = new Set<string>();
  /** Whether the name is new to the row; records it when it is. */
  const isNewName = (name: string): boolean => {
    const key = name.trim().toLowerCase();
    if (listed.has(key)) return false;
    listed.add(key);
    return true;
  };

  if (target?.trim() && isNewName(target)) {
    chips.push({
      key: `primary-${target}`,
      label: titleCase(target),
      role: 'primary',
      muscles: musclesForName(target),
    });
  }

  const primaryMuscles = target ? musclesForName(target) : [];

  for (const name of secondaryMuscles ?? []) {
    if (!name.trim() || !isNewName(name)) continue;
    chips.push({
      key: `secondary-${name}`,
      label: titleCase(name),
      role: 'secondary',
      muscles: musclesForName(name).filter(
        (id) => !primaryMuscles.includes(id)
      ),
    });
  }

  return chips;
}

/** The "Selected" readout for a tapped muscle. */
export function describeSelection(
  model: MuscleMapModel,
  id: MuscleId | null | undefined
): MuscleSelection | null {
  if (!id) return null;

  const role = roleOf(model, id);
  const status =
    role === 'primary'
      ? 'Primary mover'
      : role === 'secondary'
        ? 'Secondary / assisting'
        : 'Not targeted by this exercise';

  return { id, label: MUSCLE_LABELS[id], role, status };
}
